/**
 * trendlineMath.js — Pure JS module for trendline detection.
 *
 * Conceptually distinct from sr_analyzer.py (backend) and from any
 * existing clustering logic. ZERO imports from backend modules. ZERO
 * dependencies beyond the JS standard library. 100% in-memory math.
 *
 * Public API:
 *   detectSwings(candles, margin)        -> { highs, lows }
 *   pickAscendingTail(lows)              -> [pivots] | null
 *   pickDescendingTail(highs)            -> [pivots] | null
 *   fitLine(points)                       -> { m, b } | null
 *   detectBreak(candles, line, ...)      -> idx | null
 *   computeTrendline(candles, opts)      -> { trend|null, reason }
 *
 * NEVER mutates input. All functions are pure and deterministic.
 */

// ─── Swing detection ─────────────────────────────────────────────────
// A swing high (low) at index i requires `candles[i].h` (`.l`) to be
// STRICTLY greater (less) than all `margin` neighbours on each side.
// Strict comparison avoids duplicate flat pivots.
export function detectSwings(candles, margin = 3) {
  const highs = [];
  const lows = [];
  const n = (candles || []).length;
  for (let i = margin; i < n - margin; i++) {
    const ch = candles[i].h;
    const cl = candles[i].l;
    let isHigh = true;
    let isLow = true;
    for (let k = 1; k <= margin; k++) {
      if (candles[i - k].h >= ch || candles[i + k].h >= ch) isHigh = false;
      if (candles[i - k].l <= cl || candles[i + k].l <= cl) isLow = false;
      if (!isHigh && !isLow) break;
    }
    if (isHigh) highs.push({ i, p: ch, t: candles[i].t });
    if (isLow) lows.push({ i, p: cl, t: candles[i].t });
  }
  return { highs, lows };
}

// ─── Sequence selection ──────────────────────────────────────────────
// Walk pivots from newest to oldest, build the longest tail where each
// older pivot has a strictly LOWER price (uptrend lows) or HIGHER price
// (downtrend highs). Stop at first violation. Min 2 points.
export function pickAscendingTail(lows) {
  if (!lows || lows.length < 2) return null;
  const rev = [...lows].reverse(); // newest first
  const tail = [rev[0]];
  for (let k = 1; k < rev.length; k++) {
    if (rev[k].p < tail[tail.length - 1].p) tail.push(rev[k]);
    else break;
  }
  if (tail.length < 2) return null;
  return tail.reverse(); // oldest first
}

export function pickDescendingTail(highs) {
  if (!highs || highs.length < 2) return null;
  const rev = [...highs].reverse();
  const tail = [rev[0]];
  for (let k = 1; k < rev.length; k++) {
    if (rev[k].p > tail[tail.length - 1].p) tail.push(rev[k]);
    else break;
  }
  if (tail.length < 2) return null;
  return tail.reverse();
}

// ─── Line fitting ────────────────────────────────────────────────────
// "Two-anchor" line through the FIRST and LAST pivot of the selected
// sequence. Classical TA trendline behaviour: the line passes EXACTLY
// through both extreme pivots that anchor the trend. Intermediate
// pivots remain visible (rendered as dots) and validate the direction,
// but they do NOT influence m/b. This is the deliberate choice over
// least-squares regression — see PRD entry 2026-02-18 (visual-anchor
// alignment with the dots that the user expects to touch the line).
export function fitLine(points) {
  if (!points || points.length < 2) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const dx = last.i - first.i;
  if (dx === 0) return null;
  const m = (last.p - first.p) / dx;
  const b = first.p - m * first.i;
  return { m, b };
}

// ─── Break detection ─────────────────────────────────────────────────
// After the last pivot, find the first candle whose CLOSE is beyond the
// projected line by more than `breakPct` (default 0.5%).
//   uptrend break  : close < y · (1 − breakPct)
//   downtrend break: close > y · (1 + breakPct)
export function detectBreak(candles, line, lastPivotIdx, kind, breakPct = 0.005) {
  if (!candles || !line) return null;
  for (let i = lastPivotIdx + 1; i < candles.length; i++) {
    const y = line.m * i + line.b;
    if (y <= 0) continue;
    if (kind === "up" && candles[i].c < y * (1 - breakPct)) return i;
    if (kind === "down" && candles[i].c > y * (1 + breakPct)) return i;
  }
  return null;
}

// ─── Orchestrator ────────────────────────────────────────────────────
// Returns { trend: {...} | null, reason }. The reason field surfaces
// WHY a trend was not detected — for diagnostic UI and tests.
export function computeTrendline(candles, opts = {}) {
  const margin = opts.margin ?? 3;
  const breakPct = opts.breakPct ?? 0.005;
  if (!candles || candles.length < margin * 2 + 2) {
    return { trend: null, reason: "insufficient_candles" };
  }
  const { highs, lows } = detectSwings(candles, margin);
  const upSeq = pickAscendingTail(lows);
  const downSeq = pickDescendingTail(highs);

  if (!upSeq && !downSeq) {
    return { trend: null, reason: "no_valid_sequence" };
  }

  let kind, points;
  if (upSeq && downSeq) {
    const upLast = upSeq[upSeq.length - 1].i;
    const downLast = downSeq[downSeq.length - 1].i;
    if (upLast > downLast) { kind = "up"; points = upSeq; }
    else if (downLast > upLast) { kind = "down"; points = downSeq; }
    else if (upSeq.length >= downSeq.length) { kind = "up"; points = upSeq; }
    else { kind = "down"; points = downSeq; }
  } else if (upSeq) {
    kind = "up"; points = upSeq;
  } else {
    kind = "down"; points = downSeq;
  }

  const line = fitLine(points);
  if (!line) return { trend: null, reason: "fit_failed" };

  const lastPivotIdx = points[points.length - 1].i;
  const breakIdx = detectBreak(candles, line, lastPivotIdx, kind, breakPct);
  return {
    trend: {
      kind,
      points,
      line,
      firstPivotIdx: points[0].i,
      lastPivotIdx,
      breakIdx,
      breakPrice: breakIdx != null ? (line.m * breakIdx + line.b) : null,
      breakClose: breakIdx != null ? candles[breakIdx].c : null,
      numPivots: points.length,
    },
    reason: null,
  };
}
